'use client';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useChannelReports } from '../../hooks/useChannelReports';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelMessageListView } from '../channel-info/ChannelMessageListView';
import { ChannelPanelSheet } from '../channel-info/ChannelPanelSheet';
import { ReportedMessageRow } from '../channel-info/ReportedMessageRow';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

/**
 * Reports panel — moderation queue for the channel. Renders each
 * reported message as a regular bubble plus a tag with the reason.
 * Slice 4 is read-only (no resolve / dismiss / ban actions); those
 * land later as part of the full moderation flow.
 */
export function ChannelReportsSheet({ channel, open, onOpenChange }: Props) {
  const state = useChannelReports(channel.id);
  const currentUserId = useCurrentUserId();

  return (
    <ChannelPanelSheet open={open} onOpenChange={onOpenChange} title="Reports">
      <ChannelMessageListView
        items={state.reports}
        isLoading={state.isLoading}
        error={state.error}
        errorLabel="Couldn’t load reports."
        emptyLabel="No reported messages."
        keyOf={report => report.id}
        renderRow={report => (
          <ReportedMessageRow
            channel={channel}
            report={report}
            currentUserId={currentUserId}
          />
        )}
      />
    </ChannelPanelSheet>
  );
}
